import classNames from "classnames";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import Rating from "./rating";
import Input from "components/input";
import { useMutation } from "@tanstack/react-query";
import { Button } from "components/button";
import { useState } from "react";
import api from "services/api";
import { useAppSelector } from "@/hooks/useAppSelector";
import { selectReceipt } from "@/store/receipt/selectors";
import { selectReceiptRequestBody } from "@/store/cart/selectors";

interface ReviewProps {
  className: string;
}

const Review: React.FC<ReviewProps> = ({ className }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [showResolvedMessage, setShowResolvedMessage] = useState(false);
  const receipt = useAppSelector(selectReceipt);
  const receiptRequestBody = useAppSelector(selectReceiptRequestBody);

  const onSendReview = async () => {
    if (!receipt) return;
    if (!receiptRequestBody) return null;
    receiptRequestBody.receipt.review = { rating, message };
    await api.receipts.update(receipt.id, receiptRequestBody);
  };

  const { mutate: sendReview, reset: resetReview } = useMutation(onSendReview);

  const onMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetReview();
    setMessage(e.target.value);
  };

  const onClick = () => {
    setShowResolvedMessage(true);
    sendReview();
  };

  return (
    <div className={classNames(className, styles.container)}>
      {showResolvedMessage ? (
        <span className={classNames("h4", styles.title)} data-cy="thank-you">
          {t("successPage.thanks")}
        </span>
      ) : (
        <div>
          <span className={classNames("h4", styles.title)}>{t("successPage.review")}</span>
          <p>{t("successPage.selectStars")}</p>
          <Rating rating={rating} setRating={setRating} />
          <Input
            placeholder={t("successPage.tip")}
            containerClassName={styles.inputContainer}
            hideRemoveButton={true}
            value={message}
            onChange={onMessageChange}
            data-cy="message-input"
            maxLength={400}
          />
          <Button
            text={t("successPage.sendReview")}
            size="x-small"
            cta={false}
            disabled={rating === 0}
            onClick={onClick}
            data-cy="send-review-button"
          />
        </div>
      )}
    </div>
  );
};

export default Review;
